import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Circle, Square, Save, Trash2, Download, Lock,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { formatTime } from '../audio/audioCodec';

// Single-track recorder card: timer + waveform/preview + record/stop/
// save/download/discard controls. The Save dialog is rendered inline
// as an overlay when showSaveDialog is true. Pure presentational - all
// state and handlers come from useMicCapture + useRecordingsApi.

const SingleTrackRecorderCard = ({
  // mic state
  isRecording,
  audioUrl,
  audioBlob,
  recordingTime,
  audioPreviewRef,
  micPermission,
  // mic actions
  onStart,
  onStop,
  onDiscard,
  // save dialog
  showSaveDialog,
  setShowSaveDialog,
  savingTitle,
  setSavingTitle,
  onSave, // async (): boolean (true if persisted)
  // library actions
  onDownloadCurrent,
  isAtFreeLimit,
  isPremium,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#121212] border border-white/5 rounded-3xl p-8 mb-8"
      >
        {/* Timer Display */}
        <div className="text-center mb-8">
          <div className={`font-mono text-7xl font-bold ${isRecording ? 'text-red-400' : 'text-white'}`}>
            {formatTime(recordingTime)}
          </div>
          {isRecording && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-red-400 text-sm uppercase tracking-wide">Recording</span>
            </div>
          )}
        </div>

        {/* Waveform / preview audio element */}
        <div className="h-24 bg-[#0A0A0A] rounded-lg mb-8 flex items-center justify-center overflow-hidden">
          {isRecording ? (
            <div className="flex items-center gap-1">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-red-500 rounded-full animate-string-vibrate"
                  style={{
                    height: `${Math.random() * 60 + 20}px`,
                    animationDelay: `${i * 0.05}s`,
                  }}
                />
              ))}
            </div>
          ) : audioUrl ? (
            <audio ref={audioPreviewRef} src={audioUrl} controls className="w-full max-w-md" />
          ) : (
            <div className="flex items-center gap-1 opacity-30">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="w-1 h-8 bg-zinc-600 rounded-full" />
              ))}
            </div>
          )}
        </div>

        {/* Transport */}
        <div className="flex justify-center gap-4 flex-wrap">
          {!isRecording && !audioUrl && (
            <Button
              onClick={onStart}
              data-testid="start-recording-btn"
              size="lg"
              className="bg-red-500 hover:bg-red-600 text-white px-12 py-6 rounded-full font-bold"
            >
              <Circle className="w-6 h-6 mr-2 fill-current" />
              {micPermission === 'granted' ? t('recorder.record') : 'Abilita e Registra'}
            </Button>
          )}

          {isRecording && (
            <Button
              onClick={onStop}
              data-testid="stop-recording-btn"
              size="lg"
              className="bg-zinc-700 hover:bg-zinc-600 text-white px-12 py-6 rounded-full font-bold"
            >
              <Square className="w-6 h-6 mr-2 fill-current" />
              {t('recorder.stop')}
            </Button>
          )}

          {audioUrl && !isRecording && (
            <>
              <Button
                onClick={() => setShowSaveDialog(true)}
                data-testid="save-recording-btn"
                size="lg"
                disabled={isAtFreeLimit}
                className="bg-gradient-to-r from-amber to-orange-500 text-black px-8 py-6 rounded-full font-bold disabled:opacity-50"
              >
                <Save className="w-5 h-5 mr-2" />
                {t('recorder.save')}
              </Button>

              <Button
                onClick={() => onDownloadCurrent(audioBlob)}
                data-testid="download-current-btn"
                size="lg"
                className={`px-8 py-6 rounded-full font-bold ${
                  isPremium
                    ? 'bg-blue hover:bg-blue/80 text-white'
                    : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                }`}
              >
                {isPremium ? (
                  <Download className="w-5 h-5 mr-2" />
                ) : (
                  <Lock className="w-5 h-5 mr-2" />
                )}
                Download WAV
                {!isPremium && <span className="ml-1 text-xs">(PRO)</span>}
              </Button>

              <Button
                onClick={onDiscard}
                data-testid="discard-recording-btn"
                size="lg"
                variant="outline"
                className="border-white/20 text-white px-8 py-6 rounded-full"
              >
                <Trash2 className="w-5 h-5 mr-2" />
                {t('recorder.delete')}
              </Button>
            </>
          )}
        </div>
      </motion.div>

      {/* Save dialog */}
      {showSaveDialog && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-[#121212] border border-white/10 rounded-2xl p-8 max-w-md w-full"
          >
            <h3 className="font-heading text-xl font-bold text-white mb-4">
              {t('recorder.save')}
            </h3>
            <Input
              value={savingTitle}
              onChange={(e) => setSavingTitle(e.target.value)}
              placeholder={t('recorder.recordingNamePlaceholder')}
              data-testid="recording-title-input"
              className="bg-[#0A0A0A] border-white/10 text-white mb-4"
            />
            <div className="flex gap-3">
              <Button
                onClick={onSave}
                disabled={!savingTitle.trim()}
                className="flex-1 bg-gradient-to-r from-amber to-orange-500 text-black font-semibold"
              >
                {t('common.save')}
              </Button>
              <Button
                onClick={() => setShowSaveDialog(false)}
                variant="ghost"
                className="flex-1 text-zinc-400"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default SingleTrackRecorderCard;
